import { groupWorkflowDashboards } from './workflow-dashboard-navigation';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { forkJoin, Subscription } from 'rxjs';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { UiButtonDirective } from '@template-ui/directives';
import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import {
  materialNavigationIcon,
  primeNavigationIcon,
} from '@app/core/models/navigation-icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RoleService } from '@app/shared/services/roles.service';
import {
  WorkflowDashboardEntry,
  WorkflowDashboardsService,
} from './workflow-dashboards.service';
import {
  WorkflowStartOption,
  WorkflowStartService,
} from '../workflow-start/workflow-start.service';
@Component({
  selector: 'phms-workflow-dashboard-navigation',
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    UiButtonDirective,
    EzStandardStateComponent,
  ],
  template: `<section aria-label="Workflow navigation">
      <header>
        <div>
          <h3>Workflows</h3>
          <p>
            Browse process runs, start a process, or open a worklist to continue
            existing work.
          </p>
        </div>
        <button uiButton type="button" (click)="load()" [disabled]="loading()">
          Refresh workflows
        </button>
        <a *ngIf="startOptions().some(canStart)" routerLink="/workflows/start"
          >Start a process</a
        >
        <a *ngIf="canReadRuns()" routerLink="/workflows/runs">Process runs</a>
      </header>
      <ui-standard-state
        *ngIf="error()"
        [kind]="forbidden() ? 'forbidden' : 'error'"
        [message]="
          forbidden()
            ? 'Workflows are outside your current access. Refresh workflows to check again.'
            : 'Worklists could not be loaded. Refresh workflows to try again.'
        "
      />
      <ui-standard-state
        *ngIf="loading()"
        kind="loading"
        message="Loading workflows…"
      />
      <ui-standard-state
        *ngIf="!loading() && !error() && !groups().length"
        kind="empty"
        [message]="
          canReadRuns()
            ? 'No worklists or processes are available to start. Open Process runs to find existing work.'
            : 'No workflows are available for your current access.'
        "
      />
      <section
        class="workflow-group"
        *ngFor="let group of groups()"
        [attr.aria-label]="group.name"
      >
        <div class="workflow-group__title">
          <ng-container
            [ngTemplateOutlet]="navigationIcon"
            [ngTemplateOutletContext]="{ iconName: group.iconKey }"
          ></ng-container>
          <h4>{{ group.name }}</h4>
        </div>
        <ui-standard-state
          *ngIf="!group.entries.length"
          kind="empty"
          message="Open its runs to continue existing work or review its history."
        />
        <a
          *ngIf="canReadRuns()"
          class="runs-link"
          routerLink="/workflows/runs"
          [queryParams]="{ workflowCode: group.workflowCode }"
          >View runs</a
        >
        <a
          class="start-link"
          *ngIf="group.canStart"
          routerLink="/workflows/start"
          [queryParams]="{ workflowCode: group.workflowCode }"
          >Start {{ group.name }}</a
        >
        <ul>
          <li *ngFor="let entry of group.entries">
            <div>
              <div class="entry-title">
                <ng-container
                  *ngIf="entry.menuIconKey"
                  [ngTemplateOutlet]="navigationIcon"
                  [ngTemplateOutletContext]="{ iconName: entry.menuIconKey }"
                ></ng-container>
                <a
                  [routerLink]="entry.route"
                  [attr.aria-label]="
                    group.name +
                    ' · ' +
                    entry.stepLabel +
                    ' · ' +
                    entryLabel(entry)
                  "
                  >{{ entryLabel(entry) }}</a
                >
              </div>
              <p class="entry-context">Step: {{ entry.stepLabel }}</p>
            </div>
            <small>
              {{
                entry.selectedForNewWork ? 'Current selection' : 'Retained work'
              }}
            </small>
          </li>
        </ul>
      </section>
    </section>
    <ng-template #navigationIcon let-iconName="iconName">
      @if (primeIcon(iconName); as iconClass) {
        <i class="navigation-icon" [ngClass]="iconClass" aria-hidden="true"></i>
      } @else {
        <mat-icon class="navigation-icon" aria-hidden="true">{{
          materialIcon(iconName)
        }}</mat-icon>
      }
    </ng-template>`,
  styles: [
    `
      section[aria-label='Workflow navigation'] {
        padding: 1rem;
        border: 1px solid var(--custom-border-color, #ccd8df);
        border-radius: 6px;
        margin-bottom: 1rem;
      }
      header {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      h3,
      p {
        margin: 0 0 6px;
      }
      ul {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      li {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding: 10px 0;
        border-top: 1px solid var(--custom-border-color, #d5e0e7);
      }
      small {
        margin-left: auto;
      }
      .workflow-group {
        margin-top: 1rem;
      }
      .workflow-group__title,
      .entry-title {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .navigation-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }
      h4 {
        margin: 0 0 4px;
      }
      .entry-context {
        margin: 4px 0 0;
        font-size: 0.9rem;
      }
      button {
        font: inherit;
        padding: 6px 10px;
      }
      a {
        font-weight: 600;
      }
    `,
  ],
})
export class WorkflowDashboardNavigationComponent {
  private readonly service = inject(WorkflowDashboardsService);
  private readonly starts = inject(WorkflowStartService);
  private readonly roles = inject(RoleService);
  readonly canReadRuns = computed(() =>
    this.roles.hasAnyPermission('workflow_runtime.read'),
  );
  private readonly destroy = inject(DestroyRef);
  readonly entries = signal<WorkflowDashboardEntry[]>([]);
  readonly startOptions = signal<WorkflowStartOption[]>([]);
  readonly canStart = (option: WorkflowStartOption) => option.canStart;
  readonly primeIcon = primeNavigationIcon;
  readonly materialIcon = materialNavigationIcon;
  readonly entryLabel = (entry: WorkflowDashboardEntry) =>
    entry.menuLabel?.trim() || entry.label;
  readonly groups = computed(() => {
    const grouped = new Map<string, WorkflowDashboardEntry[]>();
    for (const entry of this.entries()) {
      const entries = grouped.get(entry.workflowCode) ?? [];
      entries.push(entry);
      grouped.set(entry.workflowCode, entries);
    }
    for (const option of this.startOptions()) {
      if (
        !grouped.has(option.workflowCode) &&
        (option.canStart || this.canReadRuns())
      )
        grouped.set(option.workflowCode, []);
    }
    return [...grouped]
      .map(([workflowCode, entries]) => ({
        workflowCode,
        canStart: this.startOptions().some(
          (option) => option.workflowCode === workflowCode && option.canStart,
        ),
        name:
          this.startOptions().find(
            (option) => option.workflowCode === workflowCode,
          )?.name ||
          (
            entries.find(
              (entry) => entry.selectedForNewWork && entry.workflowName?.trim(),
            ) ?? entries.find((entry) => entry.workflowName?.trim())
          )?.workflowName?.trim() ||
          workflowCode,
        iconKey:
          (
            entries.find(
              (entry) =>
                entry.selectedForNewWork && entry.workflowIconKey?.trim(),
            ) ?? entries.find((entry) => entry.workflowIconKey?.trim())
          )?.workflowIconKey || 'account_tree',
        entries: entries
          .map((entry, index) => ({ entry, index }))
          .sort(
            (left, right) =>
              (left.entry.menuSortOrder ?? left.index) -
                (right.entry.menuSortOrder ?? right.index) ||
              left.index - right.index,
          )
          .map(({ entry }) => entry),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  });
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly forbidden = signal(false);
  private generation = 0;
  private request?: Subscription;
  constructor() {
    this.load();
  }
  load(): void {
    const generation = ++this.generation;
    this.request?.unsubscribe();
    this.loading.set(true);
    this.error.set(false);
    this.forbidden.set(false);
    this.entries.set([]);
    this.startOptions.set([]);
    this.request = forkJoin({
      startOptions: this.starts.list(),
      entries: this.service.list(),
    })
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: ({ entries, startOptions }) => {
          if (generation !== this.generation) return;
          this.entries.set(groupWorkflowDashboards(entries));
          this.startOptions.set(startOptions);
          this.loading.set(false);
        },
        error: (error: unknown) => {
          if (generation === this.generation) {
            this.forbidden.set(
              error instanceof HttpErrorResponse &&
                [401, 403].includes(error.status),
            );
            this.error.set(true);
            this.loading.set(false);
          }
        },
      });
  }
}
